import * as yup from 'yup';
import { Formik, Form } from 'formik';

import { useMainMachine } from '../../../utils/useMainMachine';
import { Modal } from '../../../modal/Modal';
import { Button } from '../basic-ui/Button';
import { Input } from '../basic-ui/Input';
import { Spinner } from '../basic-ui/Spinner';
import { useAuthMachine } from '../../../utils/useAuthMachine';
import { AUTH_STATES, AUTH_ACTIONS, ACTIONS, STATES } from '../../../state';

const validationSchema = yup.object().shape({
  email: yup.string().required('Please provide an email.').email('Please provide a valid email.'),
});

export function AuthModal() {
  const { useSelector, machine } = useMainMachine();

  const visible = useSelector((state) =>
    state.matches({ [STATES.INITIALIZED]: { [STATES.GENERAL]: STATES.AUTHENTICATING } }),
  );

  return (
    <Modal
      dialogClassName="!top-[150px] !translate-y-0"
      onDismiss={() =>
        machine.send({
          type: ACTIONS.CANCEL,
        })
      }
    >
      {visible ? <Content /> : null}
    </Modal>
  );
}

function Content() {
  const { authMachine, useAuthMachineSelector } = useAuthMachine();
  const needsApproval = useAuthMachineSelector((state) => state.matches(AUTH_STATES.NEEDS_APPROVAL));
  const needsToVerify = useAuthMachineSelector((state) => state.matches(AUTH_STATES.NEEDS_VERIFICATION));
  const isLoading = useAuthMachineSelector(
    (state) => state.matches(AUTH_STATES.VERIFYING_OR_CREATING_USER) || state.matches(AUTH_STATES.CHECKING_PERMISSIONS),
  );

  const userEmail = useAuthMachineSelector(({ context }) => context.userEmail);

  if (isLoading) {
    return (
      <div className="flex justify-center w-full text-base text-white font-semibold space-x-2 items-center my-10">
        <Spinner className="w-4 h-4" />
        <span>Checking account...</span>
      </div>
    );
  }

  if (needsApproval) {
    return (
      <>
        <Modal.Title>Permission required</Modal.Title>
        <div className="text-sm mt-7 text-gray-200">
          A request has been sent to the site owner to add you to the account for this website. You will receive an
          email to login once you are approved.
        </div>
        <div className="flex justify-center w-full mt-10 text-sm text-white font-semibold space-x-2 items-center">
          <Spinner className="w-3.5 h-3.5" />
          <span>Waiting for approval...</span>
        </div>
      </>
    );
  }

  if (needsToVerify) {
    return (
      <>
        <Modal.Title>Verify your email</Modal.Title>
        <div className="text-sm mt-7 text-gray-200">Click the link sent to {userEmail} to login.</div>
        <div className="flex justify-end w-full mt-[30px] text-xs">
          <button className="hover:text-white" onClick={() => authMachine.send({ type: AUTH_ACTIONS.CLEAR_EMAIL })}>
            Use another email
          </button>
        </div>
      </>
    );
  }

  return <EnterEmail />;
}

function EnterEmail() {
  const { machine } = useMainMachine();
  const { authMachine } = useAuthMachine();

  return (
    <>
      <Modal.Title>Enter your email to login/signup</Modal.Title>
      <Formik
        initialValues={{ email: '' }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          authMachine.send({ type: AUTH_ACTIONS.SUBMIT_EMAIL, userEmail: values.email });
        }}
      >
        {({ values, errors, touched, handleBlur, handleChange }) => (
          <Form className="mt-7">
            <Input
              label="Email"
              placeholder="bob@example.com"
              name="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && errors.email ? errors.email : null}
            />

            <div className="text-xs leading-snug mt-2.5">
              By clicking &apos;Continue&apos; you agree to Crazy Egg&apos;s{' '}
              <a
                href="https://www.crazyegg.com/terms"
                target="_blank"
                rel="noreferrer"
                className="text-white hover:underline"
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                href="https://www.crazyegg.com/privacy"
                target="_blank"
                rel="noreferrer"
                className="text-white hover:underline"
              >
                Privacy Policy
              </a>
              .
            </div>

            <Modal.Actions>
              <Modal.Cancel
                onClick={() =>
                  machine.send({
                    type: ACTIONS.CANCEL,
                  })
                }
              />
              <Button type="submit" size="lg">
                Continue
              </Button>
            </Modal.Actions>
          </Form>
        )}
      </Formik>
    </>
  );
}
